import { Component, HostListener, input, signal, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ShareToUsersComponent } from 'src/app/@shared/components/share-to-users/share-to-users.component';
import { ShareContractService } from 'src/app/contract-management/services/share-contract.service';
import { IShareUser, SHARE_ACCESS, SHARE_FEATURE } from 'src/app/@core/models/model';
import { ToastService } from 'src/app/@core/services/toast.service';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { environment } from 'src/environments/environment.dev';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { TooltipModule } from "primeng/tooltip";
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-share-thread',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        DialogComponent,
        ShareToUsersComponent,
        IconComponent,
        ButtonComponent,
        TooltipModule
    ],
    templateUrl: './share-thread.component.html',
    styleUrl: './share-thread.component.scss'
})
export class ShareThreadComponent implements OnInit {
    threadId = input<string>('')
    threadMetadataId = input<string>('')
    shareDialog: boolean = false
    isMobile = signal<boolean>(window.innerWidth < 768);

    @HostListener('window:resize')
    onResize() {
        this.isMobile.set(window.innerWidth < 768);
    }
    shareDetails: { users: IShareUser[], anyone: SHARE_ACCESS } = { users: [], anyone: SHARE_ACCESS.RESTRICTED };
    shareUrl: string = ''
    environment = environment

    constructor(
        private shareService: ShareContractService,
        private toastService: ToastService
    ) {
    }

    ngOnInit() {
        this.shareUrl = `${window.location.origin}/shared-thread?threadId=${this.threadId()}&threadMetadataId=${this.threadMetadataId()}`
        this.getShareDetails()
    }

    getShareDetails() {
        // Guard against empty threadId/threadMetadataId
        if (!this.threadId()) return;

        this.shareService.getShareDetails(this.threadId(), SHARE_FEATURE.GENERAL_AGENT).subscribe(res => {
            // A thread that has never been shared has no Share doc yet — the API
            // still returns success:true with data:null in that case, so fall back
            // to the same default shown before any share details are fetched.
            if (res.success) {
                this.shareDetails = res.data ?? { users: [], anyone: SHARE_ACCESS.RESTRICTED }
            }
        })
    }

    openShareDialog() {
        this.getShareDetails()
        this.shareDialog = true
    }

    onCloseShareDialog() {
        this.shareDialog = false
    }

    addUserToShare(shareUser: Partial<IShareUser>) {
        this.shareService.addUserToShare(this.threadId(), shareUser, SHARE_FEATURE.GENERAL_AGENT).subscribe(res => {
            if (res.success) {
                this.shareDetails = res.data
                this.toastService.success('Member added successfully')
                this.getShareDetails() // Refresh share details
            }
        })
    }

    removeUserFromShare(shareUser: Partial<IShareUser>) {
        this.shareService.removeUserFromShare(this.threadId(), shareUser._id as string, SHARE_FEATURE.GENERAL_AGENT).subscribe(res => {
            if (res.success) {
                this.toastService.success('Member removed successfully')
                this.getShareDetails() // Refresh share details
            }
        })
    }

    updateSharedUser(shareUser: IShareUser) {
        this.shareService.updateShareUser(this.threadId(), shareUser, SHARE_FEATURE.GENERAL_AGENT).subscribe(res => {
            if (res.success) {
                this.shareDetails = res.data
                this.toastService.success('Member updated successfully')
            }
        })
    }

    changeAnyoneAccess(access: SHARE_ACCESS) {
        this.shareService.updateAnyoneAccess(this.threadId(), access, SHARE_FEATURE.GENERAL_AGENT).subscribe(res => {
            if (res.success) {
                this.shareDetails = res.data
                this.toastService.success(access === SHARE_ACCESS.VIEW ? 'Anyone with the link can now view this thread' : 'Thread access restricted')
            }
        })
    }

    copyShareUrlToClipboard() {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
            this.toastService.info('Copied to clipboard')
        }).catch(err => {
            console.error('Failed to copy text: ', err)
        })
    }
}
